import { reset, startSubmit, stopSubmit } from 'redux-form';
import { all, call, put, select } from 'redux-saga/effects';
import { displayMessage } from 'utils/request/displayMessage';
import { requestForSaga } from 'utils/request/request';
import { requestCustomErrorMessage } from 'utils/request/requestCustomErrorMessage';
import { requestMessage } from 'utils/request/requestMessage';
import {
  ADD_ENABLE_PWD,
  ADD_PWD,
  DELETE_ENABLE_PWD,
  DELETE_PWD,
  FETCH_ENABLE_PWDLIST,
  FETCH_PWDLIST,
  LOAD_ENABLE_PWD,
  LOAD_PWDLIST,
  SORT_ROWS_ENABLE_PWD,
  SORT_ROWS_PWD,
  UPDATE_ENABLE_PWD,
  UPDATE_PWD,
} from './constants';

import { isNullable } from '@ipfabric/utils';
import { takeEvery } from 'containers/ErrorBoundary';
import { Map as ImmutableMap } from 'immutable';
import get from 'lodash/get';
import { assoc, dissoc, pipe } from 'ramda';
import { generateGuid } from 'utils/guid';

function* fetchAuthentication({ type, requestUrl }) {
  const datalist = yield call(requestForSaga, {
    url: requestUrl,
    method: 'GET',
  });
  yield put({
    type: `${type}_SUCCESS`,
    payload: {
      datalist,
      loadData: true,
    },
  });
}

export function* fetchPwdList(type) {
  yield call(fetchAuthentication, {
    type: type || LOAD_PWDLIST,
    requestUrl: '/settings/credentials',
  });
}

function* fetchPwdListSaga() {
  try {
    // Dispatch action to set the loading state to true
    yield put({ type: `${LOAD_PWDLIST}_START` });

    // Perform the API call
    yield call(fetchPwdList);

    yield call(
      displayMessage,
      'success',
      'deviceCredentials.request.globalSettingsApplied',
    );
  } catch (error) {
    // Dispatch failure action to set the loading state to false
    yield put({ type: `${LOAD_PWDLIST}_ERROR` });

    // Handle the error
    yield call(requestCustomErrorMessage, 'danger', error);
    throw error;
  }
}

export function* fetchEnablePwdList(type) {
  yield call(fetchAuthentication, {
    type: type || LOAD_ENABLE_PWD,
    requestUrl: '/settings/privileges',
  });
}

function* fetchEnablePwdListSaga() {
  try {
    // Dispatch action to set the loading state to true
    yield put({ type: `${LOAD_ENABLE_PWD}_START` });

    // Perform the API call
    yield call(fetchEnablePwdList);

    yield call(
      displayMessage,
      'success',
      'deviceCredentials.request.globalSettingsApplied',
    );
  } catch (error) {
    // Dispatch failure action to set the loading state to false
    yield put({ type: `${LOAD_ENABLE_PWD}_ERROR` });

    // Handle the error
    yield call(requestCustomErrorMessage, 'danger', error);
    throw error;
  }
}

function* addPwdSaga(action) {
  try {
    yield put(startSubmit('settingsAuthenticationUserPwd'));
    const system = get(action, 'payload.system');
    if (system) {
      yield call(requestForSaga, {
        url: '/settings/credentials',
        method: 'POST',
        data: dissoc('system', action.payload),
      });
    }
    yield call(requestMessage, 'success', 'cliAuthenticationAdd');
    const currentPwdList = yield select((s) =>
      s.getIn(['deviceCredentials', 'pwdList']),
    );
    const pwdList = system
      ? yield call(requestForSaga, {
          url: '/settings/credentials',
          method: 'GET',
        })
      : {
          data: currentPwdList
            .push(
              new ImmutableMap(
                pipe(
                  assoc('id', generateGuid()),
                  assoc(
                    'priority',
                    currentPwdList.reduce(
                      (acc, item) =>
                        item.get('priority', 0) >= acc
                          ? item.get('priority') + 1
                          : acc,
                      1,
                    ),
                  ),
                  dissoc('system'),
                  assoc('custom', true),
                )(action.payload),
              ),
            )
            .toJS(),
        };
    yield put({
      type: `${ADD_PWD}_SUCCESS`,
      payload: pwdList,
    });
    yield put(stopSubmit('settingsAuthenticationUserPwd'));
    yield put(reset('settingsAuthenticationUserPwd'));
  } catch (e) {
    yield put(stopSubmit('settingsAuthenticationUserPwd'));
    if (get(e, 'code') === 'API_AUTHENTICATION_CONFLICT') {
      yield call(
        displayMessage,
        'warning',
        'deviceCredentials.request.authenticationConflict',
      );
    } else {
      yield call(requestCustomErrorMessage, 'danger', e);
      throw e;
    }
  }
}

function* updatePwdSaga(action) {
  const {
    id,
    syslog,
    network,
    notes,
    system,
    password,
    expirationDate,
    excludeNetworks,
  } = action.payload;
  try {
    if (system) {
      yield call(requestForSaga, {
        url: `/settings/credentials/${id}`,
        method: 'PATCH',
        data: {
          syslog,
          network,
          notes,
          expirationDate,
          excludeNetworks,
          ...(typeof password === 'string' &&
            password.length > 0 && { password }),
        },
      });
    }
    yield put({
      type: `${UPDATE_PWD}_SUCCESS`,
      payload: action.payload,
    });
    if (system) {
      yield call(requestMessage, 'success', 'cliAuthenticationUpdate');
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    yield put(stopSubmit('settingsAuthenticationUserPwd'));
    if (get(e, 'code') !== 'API_VALIDATION_FAILED') {
      throw e;
    }
  }
}

function* updateEnablePwdSaga(action) {
  const {
    id,
    notes,
    system,
    password,
    expirationDate,
    excludeNetworks,
    includeNetworks,
    username,
  } = action.payload;
  try {
    if (system) {
      yield call(requestForSaga, {
        url: `/settings/privileges/${id}`,
        method: 'PATCH',
        data: {
          notes,
          expirationDate,
          excludeNetworks,
          includeNetworks,
          username,
          ...(typeof password === 'string' &&
            password.length > 0 && { password }),
        },
      });
    }
    yield put({
      type: `${UPDATE_ENABLE_PWD}_SUCCESS`,
      payload: action.payload,
    });
    if (system) {
      yield call(requestMessage, 'success', 'cliAuthenticationUpdate');
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    yield put(stopSubmit('settingsAuthenticationUserPwd'));
    if (get(e, 'code') !== 'API_VALIDATION_FAILED') {
      throw e;
    }
  }
}

function getDeleteData(id, list) {
  return {
    data: list.filter((item) => item.get('id') !== id).toJS(),
  };
}

function* deletePwdSaga(action) {
  const { id, system } = action.payload;
  try {
    const data = [id];
    if (system) {
      yield call(requestForSaga, {
        url: '/settings/credentials',
        method: 'DELETE',
        data,
      });
    }
    const currentPwdList = yield select((s) =>
      s.getIn(['deviceCredentials', 'pwdList']),
    );
    const pwdList = system
      ? yield call(requestForSaga, {
          url: '/settings/credentials',
          method: 'GET',
        })
      : getDeleteData(id, currentPwdList);
    yield put({
      type: `${DELETE_PWD}_SUCCESS`,
      payload: pwdList,
    });
    if (system) {
      yield call(requestMessage, 'success', 'cliAuthenticationDel');
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* addEnablePwdSaga(action) {
  try {
    yield put(startSubmit('settingsAuthenticationEnablePwd'));
    const system = get(action, 'payload.system');
    if (system) {
      yield call(requestForSaga, {
        url: '/settings/privileges',
        method: 'POST',
        data: dissoc('system', action.payload),
      });
    }
    const currentEnablePwdList = yield select((s) =>
      s.getIn(['deviceCredentials', 'enablePwdList']),
    );
    const enablePwdList = system
      ? yield call(requestForSaga, {
          url: '/settings/privileges',
          method: 'GET',
        })
      : {
          data: currentEnablePwdList
            .push(
              new ImmutableMap(
                pipe(
                  assoc('id', generateGuid()),
                  assoc(
                    'priority',
                    currentEnablePwdList.reduce(
                      (acc, item) =>
                        item.get('priority', 0) >= acc
                          ? item.get('priority') + 1
                          : acc,
                      1,
                    ),
                  ),
                  dissoc('system'),
                  assoc('custom', true),
                )(action.payload),
              ),
            )
            .toJS(),
        };
    yield put({
      type: `${ADD_ENABLE_PWD}_SUCCESS`,
      payload: enablePwdList,
    });
    if (system) {
      yield call(requestMessage, 'success', 'cliAuthenticationAdd');
    }
    yield put(stopSubmit('settingsAuthenticationEnablePwd'));
    yield put(reset('settingsAuthenticationEnablePwd'));
  } catch (e) {
    yield put(stopSubmit('settingsAuthenticationEnablePwd'));
    if (get(e, 'code') === 'API_AUTHENTICATION_CONFLICT') {
      yield call(
        displayMessage,
        'warning',
        'deviceCredentials.request.authenticationConflictEnable',
      );
    } else {
      yield call(requestCustomErrorMessage, 'danger', e);
      throw e;
    }
  }
}

function* deleteEnablePwdSaga(action) {
  const { id, system } = action.payload;
  try {
    const enablePwdListSelected = isNullable(id)
      ? yield select((s) =>
          s.getIn(['deviceCredentials', 'enablePwdListSelected']),
        )
      : null;
    const data = isNullable(id)
      ? enablePwdListSelected.reduce((acc, _val, key) => acc.concat(key), [])
      : [id];
    if (system) {
      yield call(requestForSaga, {
        url: '/settings/privileges',
        method: 'DELETE',
        data,
      });
    }
    const currentEnablePwdList = yield select((s) =>
      s.getIn(['deviceCredentials', 'enablePwdList']),
    );
    const enablePwdList = system
      ? yield call(requestForSaga, {
          url: '/settings/privileges',
          method: 'GET',
        })
      : getDeleteData(id, currentEnablePwdList);
    yield put({
      type: `${DELETE_ENABLE_PWD}_SUCCESS`,
      payload: enablePwdList,
    });
    if (system) {
      yield call(requestMessage, 'success', 'cliAuthenticationDel');
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* sortRowsPwdSaga(action) {
  const { data, dragged, target, system } = action.payload;
  try {
    const [draggedItem] = data.splice(dragged, 1);
    data.splice(target, 0, draggedItem);
    const newData = data.map((item, index) => ({
      ...item,
      priority: index + 1,
    }));
    if (system) {
      yield call(requestForSaga, {
        url: '/settings/credentials',
        method: 'PATCH',
        data: newData.map(({ id, priority }) => ({ id, priority })),
      });
    }
    yield put({
      type: `${SORT_ROWS_PWD}_SUCCESS`,
      payload: { data: newData },
    });
    if (system) {
      yield call(requestMessage, 'success', 'passwordsListUpdated');
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* sortRowsEnablePwdSaga(action) {
  const { data, dragged, target, system } = action.payload;
  try {
    const [draggedItem] = data.splice(dragged, 1);
    data.splice(target, 0, draggedItem);
    const newData = data.map((item, index) => ({
      ...item,
      priority: index + 1,
    }));
    if (system) {
      yield call(requestForSaga, {
        url: '/settings/privileges',
        method: 'PATCH',
        data: newData.map(({ id, priority }) => ({ id, priority })),
      });
    }
    yield put({
      type: `${SORT_ROWS_ENABLE_PWD}_SUCCESS`,
      payload: { data: newData },
    });
    if (system) {
      yield call(requestMessage, 'success', 'passwordsListUpdated');
    }
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* defaultSaga() {
  yield all([
    takeEvery(ADD_PWD, addPwdSaga),
    takeEvery(UPDATE_PWD, updatePwdSaga),
    takeEvery(UPDATE_ENABLE_PWD, updateEnablePwdSaga),
    takeEvery(DELETE_PWD, deletePwdSaga),
    takeEvery(ADD_ENABLE_PWD, addEnablePwdSaga),
    takeEvery(DELETE_ENABLE_PWD, deleteEnablePwdSaga),
    takeEvery(SORT_ROWS_PWD, sortRowsPwdSaga),
    takeEvery(SORT_ROWS_ENABLE_PWD, sortRowsEnablePwdSaga),
    takeEvery(FETCH_ENABLE_PWDLIST, fetchEnablePwdListSaga),
    takeEvery(FETCH_PWDLIST, fetchPwdListSaga),
  ]);
}

export default defaultSaga;
